/**
 * ImportExportModal Component
 *
 * Modal for importing/exporting dapp configurations as JSON.
 * Allows backing up and transferring dapp registry state.
 *
 * @module containers/AdminMintingDapps/components/ImportExportModal
 * @category Admin
 */

import React, { useState } from 'react';

/**
 * Props for ImportExportModal
 */
export interface ImportExportModalProps {
  /** Callback when import completes */
  onImport: (json: string) => void;
  /** Callback to get export data */
  onExport: () => string;
  /** Callback when modal closed */
  onClose: () => void;
  /** Current number of dapps */
  dappCount: number;
}

/**
 * ImportExportModal - Import/export dapp registry as JSON.
 */
const ImportExportModal: React.FC<ImportExportModalProps> = ({
  onImport,
  onExport,
  onClose,
  dappCount,
}) => {
  const [activeTab, setActiveTab] = useState<'export' | 'import'>('export');
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [exportText, setExportText] = useState('');
  const [copied, setCopied] = useState(false);

  // Generate export on open
  React.useEffect(() => {
    try {
      const data = onExport();
      setExportText(data);
    } catch (error) {
      console.error('Failed to generate export:', error);
      setExportText('// Error generating export');
    }
  }, [onExport]);

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to copy to clipboard');
    }
  };

  // Handle download
  const handleDownload = () => {
    const blob = new Blob([exportText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `minting-dapps-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Handle import
  const handleImport = () => {
    setImportError(null);

    if (!importText.trim()) {
      setImportError('Please paste JSON data to import');
      return;
    }

    try {
      const parsed = JSON.parse(importText);

      if (!Array.isArray(parsed)) {
        throw new Error('Imported data must be an array of dapp objects');
      }

      // Validate structure
      const invalidItems = parsed.filter((item: any) => !item.id || !item.name || !item.type);
      if (invalidItems.length > 0) {
        throw new Error(`Found ${invalidItems.length} invalid dapp(s) missing required fields (id, name, type)`);
      }

      // Count existing IDs to warn about conflicts
      const existingIds = new Set(JSON.parse(exportText).map((d: any) => d.id));
      const conflicts = parsed.filter((d: any) => existingIds.has(d.id));

      if (conflicts.length > 0 && !confirm(`Import will skip ${conflicts.length} dapp(s) with duplicate IDs. Continue?`)) {
        return;
      }

      onImport(importText);
    } catch (error: any) {
      setImportError(error.message || 'Invalid JSON format');
    }
  };

  // Clear storage
  const handleClearStorage = () => {
    if (confirm('Clear backup storage? This will remove the localStorage backup but keep current registry until reload.')) {
      if (window.clearMintingDappsStorage) {
        window.clearMintingDappsStorage();
      }
      onClose();
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-exchange-alt me-2"></i>
              Import / Export Dapps
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {/* Tabs */}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'export' ? 'active' : ''}`}
                  onClick={() => setActiveTab('export')}
                >
                  <i className="fas fa-download me-1"></i> Export
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'import' ? 'active' : ''}`}
                  onClick={() => setActiveTab('import')}
                >
                  <i className="fas fa-upload me-1"></i> Import
                </button>
              </li>
            </ul>

            {/* Export Tab */}
            {activeTab === 'export' && (
              <div>
                <p className="text-muted">
                  Export all registered minting dapps as JSON. This file can be imported later or on another instance.
                </p>
                <div className="mb-3">
                  <label className="form-label">Current Dapps: <strong>{dappCount}</strong></label>
                </div>
                <div className="mb-3">
                  <textarea
                    className="form-control font-monospace"
                    style={{ height: '300px', fontSize: '12px' }}
                    value={exportText}
                    readOnly
                  />
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleCopy}
                  >
                    <i className={`fas fa-${copied ? 'check' : 'copy'} me-2`}></i>
                    {copied ? 'Copied!' : 'Copy to Clipboard'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleDownload}
                  >
                    <i className="fas fa-download me-2"></i>
                    Download JSON
                  </button>
                </div>
              </div>
            )}

            {/* Import Tab */}
            {activeTab === 'import' && (
              <div>
                <p className="text-muted">
                  Import dapps from a JSON file or pasted JSON text. Existing dapps with matching IDs will be skipped.
                </p>
                <div className="mb-3">
                  <label className="form-label">Paste JSON:</label>
                  <textarea
                    className={`form-control font-monospace ${importError ? 'is-invalid' : ''}`}
                    style={{ height: '300px', fontSize: '12px' }}
                    placeholder={`[
  {
    "id": "my-dapp",
    "name": "My Dapp",
    "description": "Description",
    "type": "internal",
    "version": "1.0.0",
    "author": "Your Name",
    "config": {
      "allowedChains": [19, 14],
      "requireAuth": true
    }
  }
]`}
                    value={importText}
                    onChange={(e) => {
                      setImportText(e.target.value);
                      setImportError(null);
                    }}
                  />
                  {importError && <div className="invalid-feedback d-block">{importError}</div>}
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleImport}
                    disabled={!importText.trim()}
                  >
                    <i className="fas fa-upload me-2"></i>
                    Import Dapps
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => document.getElementById('file-input')?.click()}
                  >
                    <i className="fas fa-file-upload me-2"></i>
                    Choose File
                  </button>
                  <input
                    type="file"
                    id="file-input"
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setImportText(event.target?.result as string);
                          setImportError(null);
                        };
                        reader.readAsText(file);
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer justify-content-between">
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleClearStorage}
              title="Clear localStorage backup only"
            >
              <i className="fas fa-trash-alt me-1"></i>
              Clear Backup Storage
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExportModal;
